<template>
  <div class="tenant-customer-accounts-wrap">
    <h2 v-if="customer">{{ $t('settings.customerAccounts') }}: {{ customer.name }}</h2>
    <h2 v-else>{{ $t('settings.customerAccounts') }}</h2>

    <el-row class="action-row">
      <el-col :span="12">
        <el-input
          class="table-search"
          v-model="searchTerm"
          :placeholder="$t('settings.account.search')"
          prefix-icon="el-icon-search"
          clearable
          @clear="clearSearch"
          @input="debouncedSearch">
        </el-input>
      </el-col>
      <el-col :span="12">
        <div class="account-actions">
          <base-button
            type="info"
            icon="el-icon-back"
            @click="$router.push({path: `/app/tenants/${$route.params.id}/customers`})">
            {{ $t('settings.backToCustomers') }}
          </base-button>
          <base-button
            type="primary"
            icon="el-icon-edit"
            @click="$router.push({path: `/app/tenants/${$route.params.id}/customers/${$route.params.customerId}/edit`})">
            {{ $t('settings.editCustomer') }}
          </base-button>
          <base-button
            type="success"
            icon="el-icon-plus"
            @click="$router.push({path: `/app/tenants/${$route.params.id}/customers/${$route.params.customerId}/accounts/create`})">
            {{ $t('settings.createAccount') }}
          </base-button>
          <base-button
            type="primary"
            icon="el-icon-refresh"
            :loading="loading"
            @click="loadData">
            {{ $t('settings.refresh') }}
          </base-button>
        </div>
      </el-col>
    </el-row>

    <el-tabs tab-position="top" v-model="tabPosition">
      <el-tab-pane :label="$t('settings.ldapAccounts')">
        <customer-accounts-table
          :accounts="accounts"
          :loading="loading"
          :tenantId="$route.params.id"
          :customerId="$route.params.customerId"
          @accountUpdated="loadData"
          @accountDeleted="loadData">
        </customer-accounts-table>
      </el-tab-pane>
      <el-tab-pane :label="$t('settings.localAccounts')">
        <customer-accounts-table
          :accounts="accounts"
          :show-local-accounts="true"
          :loading="loading"
          :tenantId="$route.params.id"
          :customerId="$route.params.customerId"
          @accountUpdated="loadData"
          @accountDeleted="loadData">
        </customer-accounts-table>
      </el-tab-pane>
    </el-tabs>

    <el-pagination
      v-if="totalAccounts > 0"
      class="accounts-pagination"
      @size-change="handlePageSizeChange"
      @current-change="handlePageChange"
      :current-page.sync="pagination.page"
      :page-sizes="[10, 20, 30, 50]"
      :page-size="pagination.pageSize"
      layout="total, sizes, prev, pager, next, jumper"
      :total="totalAccounts">
    </el-pagination>
  </div>
</template>

<script>
import CustomerAccountsTable from './CustomerAccountsTable';
import BaseButton from '@/components/common/BaseButton';

export default {
  name: "TenantCustomerAccounts",
  components: {
    CustomerAccountsTable,
    BaseButton
  },
  data() {
    return {
      customer: null,
      accounts: [],
      searchTerm: '',
      loading: false,
      tabPosition: '0',
      pagination: {
        page: 1,
        pageSize: 10
      },
      totalAccounts: 0,
      searchTimeout: null
    };
  },
  computed: {
    localOnly() {
      return this.tabPosition === '1';
    }
  },
  watch: {
    tabPosition() {
      this.accounts = [];
      this.loadData();
    }
  },
  mounted() {
    this.loadCustomer();
    this.loadData();
  },
  methods: {
    loadCustomer() {
      // Import customerService to get customer details
      import('@/services/CustomerService.class').then(({ customerService }) => {
        customerService.getById(this.$route.params.customerId)
          .then(response => {
            this.customer = response.data;
          })
          .catch(() => {
            this.$message.error(this.$t('settings.genericHttpError'));
          });
      });
    },
    loadData() {
      this.loading = true;

      // Import accountService to get accounts for this customer
      import('@/services/AccountService.class').then(({ accountService }) => {
        const pageParams = {
          page: this.pagination.page - 1,
          pageSize: this.pagination.pageSize,
          localOnly: this.localOnly,
          search: this.searchTerm
        };

        accountService.queryAccountsByCustomerId(
          this.$route.params.customerId,
          this.searchTerm,
          this.localOnly,
          pageParams
        )
          .then(response => {
            this.accounts = response.data.content || [];
            this.totalAccounts = response.data.totalElements || 0;
          })
          .catch(() => {
            this.$message.error(this.$t('settings.genericHttpError'));
          })
          .finally(() => {
            this.loading = false;
          });
      });
    },
    handlePageSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.page = 1;
      this.loadData();
    },
    handlePageChange() {
      this.loadData();
    },
    clearSearch() {
      this.searchTerm = '';
      this.pagination.page = 1;
      this.loadData();
    },
    debouncedSearch() {
      // Clear any existing timeout
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }

      // Set a new timeout to delay the search request
      this.searchTimeout = setTimeout(() => {
        this.pagination.page = 1; // Reset to first page when searching
        this.loadData();
      }, 300); // 300ms delay
    }
  }
}
</script>

<style scoped lang="scss">
.tenant-customer-accounts-wrap {
  min-width: 0;

  .action-row {
    margin-bottom: 20px;
  }

  .table-search {
    max-width: 400px;
    margin-bottom: 10px;
  }

  .account-actions {
    display: flex;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 10px;
  }

  .accounts-pagination {
    margin-top: 20px;
    text-align: center;
  }
}
</style>
